import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { NavItem } from '@/types/nav';
import { isNavItemActive } from '@/lib/nav/active-segment';
import { useState } from 'react';

interface SidebarNavProps {
  items: NavItem[];
  onNavigate?: () => void;
}

function NavItemComponent({ item, pathname, onNavigate, depth = 0 }: {
  item: NavItem;
  pathname: string;
  onNavigate?: () => void;
  depth: number;
}) {
  const isActive = isNavItemActive(item.path, pathname);
  const hasChildren = item.children && item.children.length > 0;
  const [isOpen, setIsOpen] = useState(hasChildren && isActive);

  // Auto-open parent when child is active
  if (hasChildren && !isOpen && isActive) {
    setIsOpen(true);
  }

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.preventDefault();
      setIsOpen(!isOpen);
      return;
    }
    onNavigate?.();
  };

  const indent = depth * 12; // 12px per level

  return (
    <div style={{ marginLeft: `${indent}px` }}>
      <Link
        to={item.path}
        onClick={handleClick}
        aria-current={isActive && !hasChildren ? 'page' : undefined}
        aria-expanded={hasChildren ? isOpen : undefined}
        className={[
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-[var(--transition)]',
          hasChildren ? 'cursor-pointer' : '',
          isActive && !hasChildren
            ? 'bg-[var(--accent-bg)] text-[var(--accent-light)] border-l-[3px] border-l-[var(--accent)]'
            : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)] hover:text-[var(--text-primary)] border-l-[3px] border-l-transparent',
        ].join(' ')}
      >
        <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate">{item.label}</span>
        {hasChildren ? (
          <ChevronDownIcon
            className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        ) : item.badge ? (
          <span className="rounded-full bg-[var(--accent)] px-2 py-0.5 text-xs text-white">
            {item.badge}
          </span>
        ) : null}
      </Link>

      {hasChildren && isOpen && (
        <div className="mt-1 space-y-1">
          {item.children!.map((child) => (
            <NavItemComponent
              key={child.id}
              item={child}
              pathname={pathname}
              onNavigate={onNavigate}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function SidebarNav({ items, onNavigate }: SidebarNavProps) {
  const { pathname } = useLocation();

  return (
    <nav aria-label="Primary navigation" className="flex flex-col gap-1 p-3">
      {items
        .filter((item) => !item.hidden)
        .map((item) => (
          <NavItemComponent
            key={item.id}
            item={item}
            pathname={pathname}
            onNavigate={onNavigate}
            depth={0}
          />
        ))}
    </nav>
  );
}